import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Star, Users, Video, MapPin, User, ExternalLink,
  Plus, X, Calendar, Clock, FileText, Download, Trash2, Upload,
  Loader, LayoutGrid, List, Settings, StickyNote, CheckCircle,
  Circle, ChevronLeft, ChevronRight, Edit2, Play, Search, Filter,
  FolderPlus, Folder, Sparkles, Mic, ChevronDown, ChevronUp
} from 'lucide-react';

// ── File type helpers ──────────────────────────────────────────────────────────

function getFileExt(filename = '') {
  return (filename.split('.').pop() || '').toLowerCase();
}

function isVideoFile(filename = '', contentType = '') {
  const ext = getFileExt(filename);
  const videoExts = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'flv', 'wmv'];
  return videoExts.includes(ext) || (contentType || '').startsWith('video/');
}

function FileTypeBadge({ filename = '', contentType = '' }) {
  const ext = getFileExt(filename);
  const map = {
    pdf:  { label: 'PDF',  bg: 'bg-red-600',    text: 'text-white' },
    doc:  { label: 'DOC',  bg: 'bg-blue-600',   text: 'text-white' },
    docx: { label: 'DOC',  bg: 'bg-blue-600',   text: 'text-white' },
    xls:  { label: 'XLS',  bg: 'bg-green-600',  text: 'text-white' },
    xlsx: { label: 'XLS',  bg: 'bg-green-600',  text: 'text-white' },
    ppt:  { label: 'PPT',  bg: 'bg-orange-500', text: 'text-white' },
    pptx: { label: 'PPT',  bg: 'bg-orange-500', text: 'text-white' },
    txt:  { label: 'TXT',  bg: 'bg-gray-500',   text: 'text-white' },
    png:  { label: 'IMG',  bg: 'bg-purple-600', text: 'text-white' },
    jpg:  { label: 'IMG',  bg: 'bg-purple-600', text: 'text-white' },
    jpeg: { label: 'IMG',  bg: 'bg-purple-600', text: 'text-white' },
    webp: { label: 'IMG',  bg: 'bg-purple-600', text: 'text-white' },
    mp4:  { label: 'MP4',  bg: 'bg-pink-600',   text: 'text-white' },
    mov:  { label: 'MOV',  bg: 'bg-pink-600',   text: 'text-white' },
  };
  const cfg = map[ext] || { label: ext.toUpperCase() || 'FILE', bg: 'bg-gray-600', text: 'text-white' };
  return (
    <div className={`w-10 h-10 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
      <span className={`text-[9px] font-bold ${cfg.text} leading-none`}>{cfg.label}</span>
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (days > 0) return `added ${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `added ${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (mins > 0) return `added ${mins} min ago`;
  return 'added just now';
}

// Dashboard card style — black bg + gray border + glow on hover
const Card = ({ children, className = '' }) => (
  <div className={`bg-black border border-gray-600 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.35)] transition-all duration-300 ${className}`}>
    {children}
  </div>
);

// Status config
const STATUS_MAP = {
  upcoming:    { label: 'Upcoming',    cls: 'bg-blue-600/20 text-blue-300 border-blue-500/40' },
  in_progress: { label: 'In Progress', cls: 'bg-green-600/20 text-green-300 border-green-500/40' },
  done:        { label: 'Completed',   cls: 'bg-gray-600/20 text-gray-300 border-gray-500/40' },
};

// Mini calendar for Events card
function MiniCalendar({ selectedDate }) {
  const [current, setCurrent] = useState(() => {
    const d = selectedDate ? new Date(selectedDate) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const year  = current.getFullYear();
  const month = current.getMonth();
  const monthLabel = current.toLocaleString('default', { month: 'long', year: 'numeric' });

  const firstDay  = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today     = new Date();
  const highlighted = selectedDate ? new Date(selectedDate).getDate() : null;
  const hlMonth   = selectedDate ? new Date(selectedDate).getMonth() : null;
  const hlYear    = selectedDate ? new Date(selectedDate).getFullYear() : null;

  // Build calendar grid (Sun-Sat)
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  const isHl    = (d) => d === highlighted && month === hlMonth && year === hlYear;

  return (
    <div className="select-none">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-white text-xs font-semibold">{monthLabel}</span>
        <div className="flex gap-1">
          <button onClick={() => setCurrent(new Date(year, month - 1, 1))}
            className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
            <ChevronLeft size={13} />
          </button>
          <button onClick={() => setCurrent(new Date(year, month + 1, 1))}
            className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
            <ChevronRight size={13} />
          </button>
        </div>
      </div>
      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
          <div key={d} className="text-center text-[10px] text-gray-500 font-medium py-0.5">{d}</div>
        ))}
      </div>
      {/* Day cells */}
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((d, i) => (
          <div key={i} className={`text-center text-xs py-1 rounded transition-colors ${
            !d ? '' :
            isHl(d)    ? 'bg-white text-black font-bold rounded-full' :
            isToday(d) ? 'border border-white/50 text-white rounded-full' :
            'text-gray-400 hover:text-white hover:bg-white/10 cursor-default'
          }`}>
            {d || ''}
          </div>
        ))}
      </div>
    </div>
  );
}

// Avatar row
function AvatarRow({ participants, max = 4 }) {
  const visible = participants.slice(0, max);
  const extra   = participants.length - max;
  return (
    <div className="flex items-center">
      {visible.map((p, i) => (
        <div key={i} className="w-7 h-7 rounded-full border-2 border-black overflow-hidden -ml-1.5 first:ml-0 flex-shrink-0 bg-gray-700">
          {p.image_url
            ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold">{p.name?.charAt(0) || '?'}</div>
          }
        </div>
      ))}
      {extra > 0 && (
        <div className="w-7 h-7 rounded-full border-2 border-black bg-gray-700 -ml-1.5 flex items-center justify-center text-[10px] text-gray-300 font-semibold flex-shrink-0">
          +{extra}
        </div>
      )}
    </div>
  );
}

// ── Documents Tab Component ──────────────────────────────────────────────────
function DocumentsTab({ meeting, uploadingDocument, documentUploadMeetingId, handleDocumentUpload, handleRemoveDocument, apiUrl }) {
  const [search, setSearch] = useState('');
  const [showAllDocs, setShowAllDocs]   = useState(false);
  const [showAllVids, setShowAllVids]   = useState(false);

  const allDocs = meeting.documents || [];

  // Filter by search
  const filtered = allDocs.filter(d =>
    !search || (d.original_filename || '').toLowerCase().includes(search.toLowerCase())
  );

  // Split into files and videos
  const fileDocs  = filtered.filter(d => !isVideoFile(d.original_filename, d.content_type));
  const videoDocs = filtered.filter(d =>  isVideoFile(d.original_filename, d.content_type));

  const DOC_PREVIEW  = 6;
  const VID_PREVIEW  = 6;
  const shownDocs = showAllDocs ? fileDocs  : fileDocs.slice(0, DOC_PREVIEW);
  const shownVids = showAllVids ? videoDocs : videoDocs.slice(0, VID_PREVIEW);

  return (
    <div className="space-y-6">
      {/* ── Header bar ── */}
      <div className="bg-black border border-gray-700 rounded-xl p-5 hover:border-gray-400 hover:shadow-[0_0_20px_rgba(156,163,175,0.25)] transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <FileText size={16} className="text-gray-400" />
            <span className="text-white font-semibold">Files</span>
            <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">{allDocs.length}</span>
          </div>
          <label
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black text-sm font-semibold cursor-pointer hover:bg-gray-100 transition-colors"
            data-testid="add-new-file-btn"
          >
            {uploadingDocument && documentUploadMeetingId === meeting.id
              ? <><Loader size={13} className="animate-spin text-black" /> Uploading…</>
              : <><Plus size={13} /> Add new</>
            }
            <input type="file" className="hidden"
              accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.mp4,.mov,.avi,.webm"
              onChange={e => { const f = e.target.files[0]; if (f) handleDocumentUpload(f, meeting.id); e.target.value = ''; }}
              disabled={uploadingDocument} />
          </label>
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search files…"
            className="w-full pl-9 pr-3 py-2 bg-black border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-400 transition-colors"
            data-testid="file-search"
          />
        </div>
      </div>

      {/* Empty state */}
      {allDocs.length === 0 && (
        <div className="bg-black border-2 border-dashed border-gray-800 rounded-xl p-16 text-center hover:border-gray-600 transition-colors">
          <FileText size={36} className="mx-auto text-gray-700 mb-3" />
          <p className="text-gray-500 text-sm font-medium">No files attached yet</p>
          <p className="text-gray-700 text-xs mt-1">Supported: PDF, DOC, XLS, PPT, TXT, Images, Videos (max 50MB)</p>
        </div>
      )}

      {/* No results when searching */}
      {search && fileDocs.length === 0 && videoDocs.length === 0 && allDocs.length > 0 && (
        <div className="text-center py-12 text-gray-600 text-sm">
          No files match &ldquo;{search}&rdquo;
        </div>
      )}

      {/* ── Documents section ── */}
      {fileDocs.length > 0 && (        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-white text-sm font-semibold">Documents</span>
            {fileDocs.length > DOC_PREVIEW && (
              <button onClick={() => setShowAllDocs(v => !v)}
                className="text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
                data-testid="show-all-docs-btn">
                {showAllDocs ? 'Show fewer' : `Show all (${fileDocs.length})`}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {shownDocs.map((doc, i) => (
              <div key={doc.id || i}
                className="flex items-center gap-3 p-3.5 bg-black border border-gray-700 rounded-xl hover:border-gray-300 hover:shadow-[0_0_16px_rgba(156,163,175,0.25)] transition-all duration-200"
                data-testid={`doc-card-${i}`}>
                <FileTypeBadge filename={doc.original_filename} contentType={doc.content_type} />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{doc.original_filename}</p>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {doc.size || '—'}
                    {(doc.created_at || doc.uploaded_at) && <> · {timeAgo(doc.created_at || doc.uploaded_at)}</>}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <a href={`${apiUrl}${doc.url}`} target="_blank" rel="noopener noreferrer"
                    className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
                    title="Download">
                    <Download size={14} />
                  </a>
                  <button onClick={() => handleRemoveDocument(meeting.id, doc.id)}
                    className="p-2 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Videos section ── */}
      {videoDocs.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-white text-sm font-semibold">Videos</span>
            {videoDocs.length > VID_PREVIEW && (
              <button onClick={() => setShowAllVids(v => !v)}
                className="text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors">
                {showAllVids ? 'Show fewer' : `Show all (${videoDocs.length})`}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shownVids.map((doc, i) => (
              <div key={doc.id || i}
                className="bg-black border border-gray-700 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-[0_0_16px_rgba(156,163,175,0.25)] transition-all duration-200 group"
                data-testid={`video-card-${i}`}>
                {/* Thumbnail area */}
                <div className="relative bg-gray-900" style={{ aspectRatio: '16/9' }}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-colors">
                      <Play size={18} className="text-white ml-0.5" fill="white" />
                    </div>
                  </div>
                  {/* Action buttons — top right */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <a href={`${apiUrl}${doc.url}`} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 rounded-lg bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 transition-colors">
                      <Download size={13} />
                    </a>
                    <button onClick={() => handleRemoveDocument(meeting.id, doc.id)}
                      className="p-1.5 rounded-lg bg-black/60 backdrop-blur-sm text-white hover:bg-red-500/80 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                {/* Meta below thumbnail */}
                <div className="p-3">
                  <p className="text-white text-sm font-medium truncate">{doc.original_filename}</p>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {doc.size || '—'}
                    {(doc.created_at || doc.uploaded_at) && <> · {timeAgo(doc.created_at || doc.uploaded_at)}</>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Transcriptions Tab Component with Folders and AI Breakdown ──────────────────
function TranscriptionsTab({ 
  meeting, 
  conversations, 
  meetingTranscriptions, 
  setMeetingTranscriptions, 
  setViewingTranscription, 
  setShowTranscriptionPopup,
  onTranscriptionUpdate,
  apiUrl 
}) {
  const [folderTranscriptions, setFolderTranscriptions] = useState([]);
  const [folderName, setFolderName] = useState(meeting.title || 'Meeting Folder');
  const [loading, setLoading] = useState(true);
  const [addingTranscription, setAddingTranscription] = useState(false);
  const [selectedTranscriptionId, setSelectedTranscriptionId] = useState('');
  const [expandedCards, setExpandedCards] = useState({});
  const [breakdowns, setBreakdowns] = useState({});
  const [loadingBreakdown, setLoadingBreakdown] = useState({});
  const [editingFolderName, setEditingFolderName] = useState(false);
  const [newFolderName, setNewFolderName] = useState(folderName);
  
  // Get user_id from meeting
  const userId = meeting.user_id;

  // Load folder data
  const loadFolder = useCallback(async () => {
    if (!userId || !meeting.id) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/meetings/${meeting.id}/transcription-folder?user_id=${userId}`);
      if (res.ok) {
        const folder = await res.json();
        setFolderName(folder.name || meeting.title || 'Meeting Folder');
        setNewFolderName(folder.name || meeting.title || 'Meeting Folder');
        
        // Map transcription IDs to actual conversation objects
        const ids = folder.transcription_ids || [];
        const transcripts = ids.map(id => conversations.find(c => c.id === id)).filter(Boolean);
        setFolderTranscriptions(transcripts);
      }
    } catch (e) {
      console.error('Failed to load folder:', e);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, meeting.id, userId, conversations, meeting.title]);

  useEffect(() => {
    loadFolder();
  }, [loadFolder]);

  // Add transcription to folder
  const handleAddToFolder = async () => {
    if (!selectedTranscriptionId) return;
    setAddingTranscription(true);
    try {
      const res = await fetch(`${apiUrl}/api/meetings/${meeting.id}/transcription-folder/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, transcription_id: selectedTranscriptionId })
      });
      if (res.ok) {
        const transcript = conversations.find(c => c.id === selectedTranscriptionId);
        if (transcript && !folderTranscriptions.find(t => t.id === selectedTranscriptionId)) {
          setFolderTranscriptions([...folderTranscriptions, transcript]);
        }
        setSelectedTranscriptionId('');
      }
    } catch (e) {
      console.error('Failed to add transcription:', e);
    } finally {
      setAddingTranscription(false);
    }
  };

  // Remove transcription from folder
  const handleRemoveFromFolder = async (transcriptionId) => {
    try {
      await fetch(`${apiUrl}/api/meetings/${meeting.id}/transcription-folder/${transcriptionId}?user_id=${userId}`, {
        method: 'DELETE'
      });
      setFolderTranscriptions(folderTranscriptions.filter(t => t.id !== transcriptionId));
    } catch (e) {
      console.error('Failed to remove transcription:', e);
    }
  };

  // Rename folder
  const handleRenameFolder = async () => {
    try {
      await fetch(`${apiUrl}/api/meetings/${meeting.id}/transcription-folder/rename`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, name: newFolderName })
      });
      setFolderName(newFolderName);
      setEditingFolderName(false);
    } catch (e) {
      console.error('Failed to rename folder:', e);
    }
  };

  // Load AI breakdown for a transcription
  const loadBreakdown = async (transcript) => {
    if (breakdowns[transcript.id] || loadingBreakdown[transcript.id]) return;
    
    setLoadingBreakdown(prev => ({ ...prev, [transcript.id]: true }));
    try {
      // Check if it's a Fireflies transcript
      const isFireflies = transcript.source === 'fireflies' || transcript.fireflies_id;
      
      if (isFireflies) {
        const ffId = transcript.fireflies_id || transcript.id;
        const res = await fetch(`${apiUrl}/api/fireflies/transcripts/${ffId}/breakdown?user_id=${userId}`);
        if (res.ok) {
          const data = await res.json();
          setBreakdowns(prev => ({ ...prev, [transcript.id]: data.breakdown }));
        }
      } else if (transcript.ai_breakdown) {
        // Manual transcript with existing breakdown
        setBreakdowns(prev => ({ ...prev, [transcript.id]: transcript.ai_breakdown }));
      }
    } catch (e) {
      console.error('Failed to load breakdown:', e);
    } finally {
      setLoadingBreakdown(prev => ({ ...prev, [transcript.id]: false }));
    }
  };

  // Toggle card expansion
  const toggleCard = (id) => {
    setExpandedCards(prev => {
      const newState = { ...prev, [id]: !prev[id] };
      // Load breakdown when expanding
      if (newState[id]) {
        const transcript = folderTranscriptions.find(t => t.id === id);
        if (transcript) loadBreakdown(transcript);
      }
      return newState;
    });
  };

  // Format date
  const formatTranscriptDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get available transcriptions (not already in folder)
  const availableTranscriptions = conversations.filter(
    c => !folderTranscriptions.find(t => t.id === c.id)
  );

  return (
    <div className="space-y-6">
      {/* Folder Header */}
      <div className="bg-black border border-gray-700 rounded-xl p-5 hover:border-gray-400 hover:shadow-[0_0_20px_rgba(156,163,175,0.25)] transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Folder size={18} className="text-purple-400" />
            {editingFolderName ? (
              <div className="flex items-center gap-2">
                <input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  className="bg-black border border-gray-600 rounded px-2 py-1 text-white text-sm"
                  autoFocus
                />
                <button 
                  onClick={handleRenameFolder}
                  className="p-1 text-green-400 hover:text-green-300"
                >
                  <CheckCircle size={16} />
                </button>
                <button 
                  onClick={() => { setEditingFolderName(false); setNewFolderName(folderName); }}
                  className="p-1 text-gray-400 hover:text-white"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-white font-semibold">{folderName}</span>
                <button 
                  onClick={() => setEditingFolderName(true)}
                  className="p-1 text-gray-500 hover:text-white"
                >
                  <Edit2 size={12} />
                </button>
              </div>
            )}
            <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">
              {folderTranscriptions.length} transcription{folderTranscriptions.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Add Transcription */}
        <div className="flex gap-2">
          <select
            value={selectedTranscriptionId}
            onChange={(e) => setSelectedTranscriptionId(e.target.value)}
            className="flex-1 bg-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gray-400"
            data-testid="add-transcription-select"
          >
            <option value="">Select transcription to add...</option>
            {availableTranscriptions.map(c => (
              <option key={c.id} value={c.id}>
                {c.title || 'Untitled'} — {formatTranscriptDate(c.date)}
              </option>
            ))}
          </select>
          <button
            onClick={handleAddToFolder}
            disabled={!selectedTranscriptionId || addingTranscription}
            className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
            data-testid="add-to-folder-btn"
          >
            {addingTranscription ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
            Add
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <Loader size={24} className="animate-spin text-gray-500 mx-auto" />
          <p className="text-gray-500 text-sm mt-2">Loading transcriptions...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && folderTranscriptions.length === 0 && (
        <div className="bg-black border-2 border-dashed border-gray-800 rounded-xl p-12 text-center hover:border-gray-600 transition-colors">
          <Mic size={36} className="mx-auto text-gray-700 mb-3" />
          <p className="text-gray-500 text-sm font-medium">No transcriptions in this folder</p>
          <p className="text-gray-700 text-xs mt-1">Select a transcription above to add it to this meeting</p>
        </div>
      )}

      {/* Transcription Cards */}
      {!loading && folderTranscriptions.length > 0 && (
        <div className="space-y-4">
          {folderTranscriptions.map((transcript) => {
            const isExpanded = expandedCards[transcript.id];
            const breakdown = breakdowns[transcript.id];
            const isLoadingBreakdown = loadingBreakdown[transcript.id];
            
            return (
              <div
                key={transcript.id}
                className="bg-black border border-gray-700 rounded-xl overflow-hidden hover:border-gray-400 hover:shadow-[0_0_16px_rgba(156,163,175,0.25)] transition-all duration-200"
                data-testid={`transcription-card-${transcript.id}`}
              >
                {/* Card Header */}
                <div 
                  className="p-4 cursor-pointer flex items-center justify-between"
                  onClick={() => toggleCard(transcript.id)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-purple-600/20 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
                      <Mic size={16} className="text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium text-sm truncate">
                        {transcript.title || 'Untitled Transcription'}
                      </h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-gray-500 text-xs">{formatTranscriptDate(transcript.date)}</span>
                        {transcript.source === 'fireflies' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-orange-500/20 text-orange-300 rounded">Fireflies</span>
                        )}
                        {transcript.source === 'manual' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded">Manual</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleRemoveFromFolder(transcript.id); }}
                      className="p-2 text-gray-600 hover:text-red-400 transition-colors"
                      title="Remove from folder"
                    >
                      <Trash2 size={14} />
                    </button>
                    {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </div>
                </div>

                {/* Expanded Content with AI Breakdown */}
                {isExpanded && (
                  <div className="border-t border-gray-800 p-4 space-y-4">
                    {/* Loading breakdown */}
                    {isLoadingBreakdown && (
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <Loader size={14} className="animate-spin" />
                        Loading AI analysis...
                      </div>
                    )}

                    {/* AI Breakdown Section */}
                    {breakdown && (
                      <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <h5 className="text-xs uppercase tracking-wider text-purple-300 flex items-center gap-2">
                            <Sparkles size={12} /> AI Breakdown
                          </h5>
                          {breakdown.sentiment && (
                            <span className="text-[10px] uppercase tracking-wider text-purple-200 bg-purple-500/20 border border-purple-400/30 px-2 py-0.5 rounded-full">
                              {breakdown.sentiment}
                            </span>
                          )}
                        </div>

                        {breakdown.overview && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Overview</div>
                            <p className="text-sm text-gray-200 leading-relaxed">{breakdown.overview}</p>
                          </div>
                        )}

                        {breakdown.key_topics?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Topics</div>
                            <div className="flex flex-wrap gap-1.5">
                              {breakdown.key_topics.map((t, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-[#161A22] border border-white/10 text-gray-300">{t}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {breakdown.key_decisions?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Key Decisions</div>
                            <ul className="space-y-1 list-disc list-inside text-sm text-gray-200">
                              {breakdown.key_decisions.map((d, i) => <li key={i}>{d}</li>)}
                            </ul>
                          </div>
                        )}

                        {breakdown.action_items?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Action Items</div>
                            <ul className="space-y-1.5 text-sm">
                              {breakdown.action_items.map((a, i) => {
                                const text = typeof a === 'string' ? a : (a?.text || JSON.stringify(a));
                                const owner = (typeof a === 'object' && a) ? a.owner : null;
                                const due = (typeof a === 'object' && a) ? a.due : null;
                                return (
                                  <li key={i} className="flex flex-col text-gray-200">
                                    <span>{text}</span>
                                    {owner && owner !== 'Unassigned' && (
                                      <span className="text-[11px] text-gray-500">→ {owner}{due ? ` · ${due}` : ''}</span>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}

                        {breakdown.open_questions?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Open Questions</div>
                            <ul className="space-y-1 list-disc list-inside text-sm text-gray-200">
                              {breakdown.open_questions.map((q, i) => <li key={i}>{q}</li>)}
                            </ul>
                          </div>
                        )}

                        {breakdown.speaker_breakdown?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Speaker Contributions</div>
                            <div className="space-y-2">
                              {breakdown.speaker_breakdown.map((sp, i) => (
                                <div key={i} className="text-sm">
                                  <span className="text-purple-300 font-medium">{sp.name}: </span>
                                  <span className="text-gray-300">{sp.summary}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {breakdown.next_steps?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Next Steps</div>
                            <ul className="space-y-1 list-disc list-inside text-sm text-gray-200">
                              {breakdown.next_steps.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* View Full Transcript Button */}
                    <button
                      onClick={() => {
                        setViewingTranscription(transcript);
                        setShowTranscriptionPopup(true);
                      }}
                      className="w-full py-2.5 border border-gray-700 rounded-lg text-white text-sm font-medium hover:border-gray-400 hover:shadow-[0_0_12px_rgba(156,163,175,0.2)] transition-all"
                      data-testid={`view-full-transcript-${transcript.id}`}
                    >
                      View Full Transcript
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Legacy Embedded Transcript */}
      {meeting.transcript && (
        <div className="bg-black border border-gray-700 rounded-xl p-5">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Embedded Transcript</p>
          <div className="bg-black/50 border border-gray-800 rounded-lg p-4 font-mono text-xs text-gray-300 leading-relaxed max-h-64 overflow-y-auto whitespace-pre-wrap">
            {meeting.transcript}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProjectDetailPanel({
  meeting,
  conversations,
  meetingTranscriptions,
  setMeetingTranscriptions,
  setViewingTranscription,
  setShowTranscriptionPopup,
  uploadingDocument,
  documentUploadMeetingId,
  handleDocumentUpload,
  handleRemoveDocument,
  getMeetingStatus,
  formatDate,
  formatTimeTo12Hour,
  getMeetingLinkType,
  getDocumentIcon,
  apiUrl,
  onClose,
  onEdit,
  onNotesUpdate,
  onTranscriptionUpdate,
}) {
  const [activeTab, setActiveTab] = useState('overview');

  // Load notes: prefer panel_notes (persisted JSON array), fall back to meeting.notes text
  const initNotes = () => {
    if (meeting.panel_notes) {
      try { return JSON.parse(meeting.panel_notes); } catch (e) { /* ignore parse error */ }
    }
    const notesSource = meeting.agenda ? (meeting.notes || '') : '';
    return notesSource.split('\n').filter(Boolean).map((n, i) => ({ id: i, text: n, done: false }));
  };

  const [notes, setNotes]     = useState(initNotes);
  const [noteInput, setNoteInput] = useState('');

  const status   = getMeetingStatus(meeting);
  const statusCfg = STATUS_MAP[status] || STATUS_MAP.upcoming;
  const participants = meeting.participants || [];
  const docs       = meeting.documents || [];

  // Progress: use the winPercentage passed from kanban card, or derive from pipeline_stage/status
  const WIN_PCT = { tbd: 25, upcoming: 50, in_progress: 75, done: 100 };
  const progress = meeting._winPercentage !== undefined
    ? meeting._winPercentage
    : WIN_PCT[meeting.pipeline_stage] ?? WIN_PCT[status] ?? 25;

  const tabs = [
    { id: 'overview',    label: 'Overview',      icon: LayoutGrid },
    { id: 'documents',   label: 'Documents',     icon: FileText },
    { id: 'transcript',  label: 'Transcription', icon: List },
    { id: 'settings',    label: 'Settings',      icon: Settings },
  ];

  const saveNotes = (updated) => {
    if (onNotesUpdate) onNotesUpdate(meeting.id, updated);
  };

  const addNote = () => {
    if (!noteInput.trim()) return;
    const updated = [...notes, { id: Date.now(), text: noteInput.trim(), done: false }];
    setNotes(updated);
    setNoteInput('');
    saveNotes(updated);
  };

  const toggleNote = (id) => {
    const updated = notes.map(n => n.id === id ? { ...n, done: !n.done } : n);
    setNotes(updated);
    saveNotes(updated);
  };

  const removeNote = (id) => {
    const updated = notes.filter(n => n.id !== id);
    setNotes(updated);
    saveNotes(updated);
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black overflow-y-auto"
      data-testid="project-detail-panel"
    >
      {/* ── TOP HEADER ── */}
      <div className="sticky top-0 z-10 bg-black border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Row 1: back + title + status + actions */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              {/* Back */}
              <button
                onClick={onClose}
                data-testid="detail-close-btn"
                className="mt-0.5 flex-shrink-0 p-2 rounded-lg border border-gray-600 hover:border-gray-300 hover:shadow-[0_0_12px_rgba(156,163,175,0.3)] text-gray-300 hover:text-white transition-all duration-200"
              >
                <ArrowLeft size={16} />
              </button>

              {/* Title + meta */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-xl font-bold text-white truncate" data-testid="detail-title">
                    {meeting.title || 'Untitled'}
                  </h1>
                  <Star size={15} className="text-yellow-400 flex-shrink-0" fill="currentColor" />
                  <span className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusCfg.cls}`} data-testid="detail-status">
                    {statusCfg.label}
                  </span>
                </div>

                {/* Progress + meta row */}
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-[160px]">
                    <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden" style={{ width: 120 }}>
                      <div className="h-full bg-white rounded-full transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">{progress}% completed</span>
                  </div>

                  {meeting.date && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar size={11} />
                      {formatDate(meeting.date)}
                      {meeting.time && <> · {formatTimeTo12Hour(meeting.time)}</>}
                    </span>
                  )}

                  {meeting.organization && (
                    <span className="text-xs text-gray-500 truncate max-w-[180px]">{meeting.organization}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Right: avatars + invite + edit */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {participants.length > 0 && <AvatarRow participants={participants} />}
              <button
                onClick={onEdit}
                data-testid="detail-edit-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-600 hover:border-gray-300 hover:shadow-[0_0_12px_rgba(156,163,175,0.3)] text-sm text-white transition-all duration-200"
              >
                <Edit2 size={13} /> Edit
              </button>
              <button
                onClick={onClose}
                className="p-2 rounded-lg border border-gray-700 hover:border-gray-400 text-gray-400 hover:text-white transition-all duration-200"
              >
                <X size={15} />
              </button>
            </div>
          </div>

          {/* Row 2: tab bar */}
          <div className="flex items-center gap-0.5 mt-4">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                data-testid={`detail-tab-${id}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-t-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === id
                    ? 'bg-white/10 text-white border border-white/20 border-b-0 -mb-px'
                    : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                }`}
              >
                <Icon size={13} />{label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div className="max-w-7xl mx-auto px-6 py-6">

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* LEFT column */}
            <div className="space-y-5">

              {/* Participants card */}
              <Card className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Users size={14} /> Participants</h3>
                  <span className="text-xs text-gray-500">{participants.length} people</span>
                </div>
                {participants.length > 0 ? (
                  <div className="space-y-2.5">
                    {participants.slice(0, 5).map((p, i) => (
                      <div key={i} className="flex items-center gap-3">
                        {p.image_url
                          ? <img src={p.image_url} alt={p.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                          : <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{p.name?.charAt(0) || '?'}</div>
                        }
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-medium truncate">{p.name}</div>
                          {(p.role || p.company) && <div className="text-gray-500 text-xs truncate">{p.role || p.company}</div>}
                        </div>
                      </div>
                    ))}
                    {participants.length > 5 && (
                      <div className="text-center pt-1">
                        <span className="text-xs text-gray-500">+{participants.length - 5} more</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-600 text-xs text-center py-4">No participants added</p>
                )}
              </Card>

              {/* Events / Calendar card */}
              <Card className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Calendar size={14} /> Schedule</h3>
                </div>
                <MiniCalendar selectedDate={meeting.date} />

                {/* Scheduled event from meeting data */}
                {meeting.date && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Scheduled</p>
                    <div className="flex items-start gap-3 p-2.5 rounded-lg bg-white/5 border border-white/10">
                      <div className="w-1 h-full min-h-[32px] rounded-full bg-white flex-shrink-0" />
                      <div>
                        <p className="text-white text-xs font-medium">{meeting.title}</p>
                        {meeting.time && <p className="text-gray-500 text-xs mt-0.5">{formatTimeTo12Hour(meeting.time)} · {meeting.duration || 60} min</p>}
                        {meeting.meeting_type && (
                          <span className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-gray-300">
                            {meeting.meeting_type === 'virtual' ? <Video size={9} /> : <MapPin size={9} />}
                            {meeting.meeting_type === 'virtual' ? 'Virtual' : 'In-Person'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Join link */}
                {meeting.meeting_link && (
                  <a href={meeting.meeting_link} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 mt-3 text-xs text-blue-400 hover:text-blue-300 transition-colors">
                    <ExternalLink size={12} /> Join {getMeetingLinkType(meeting.meeting_link)} Meeting
                  </a>
                )}
              </Card>
            </div>

            {/* MIDDLE column */}
            <div className="space-y-5">

              {/* Agenda / Description card */}
              <Card className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2"><FileText size={14} /> Agenda</h3>
                </div>
                {(meeting.agenda || meeting.notes) ? (
                  <div className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{meeting.agenda || meeting.notes}</div>
                ) : (
                  <p className="text-gray-600 text-xs text-center py-6">No agenda set</p>
                )}
              </Card>

              {/* Meeting details quick-facts */}
              <Card className="p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2"><Clock size={14} /> Details</h3>
                <div className="space-y-3">
                  {meeting.date && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Date</span>
                      <span className="text-white">{formatDate(meeting.date)}</span>
                    </div>
                  )}
                  {meeting.time && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Time</span>
                      <span className="text-white">{formatTimeTo12Hour(meeting.time)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Duration</span>
                    <span className="text-white">{meeting.duration || 60} min</span>
                  </div>
                  {meeting.location && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Location</span>
                      <span className="text-white truncate max-w-[160px]">{meeting.location}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Type</span>
                    <span className="text-white flex items-center gap-1">
                      {meeting.meeting_type === 'virtual' ? <><Video size={12} /> Virtual</> : <><MapPin size={12} /> In-Person</>}
                    </span>
                  </div>
                </div>
              </Card>
            </div>

            {/* RIGHT column */}
            <div className="space-y-5">

              {/* Overall Progress card */}
              <Card className="p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2"><Star size={14} className="text-yellow-400" /> Overall Progress</h3>
                <div className="text-center mb-4">
                  <span className="text-4xl font-bold text-white">{progress}%</span>
                  <p className="text-gray-500 text-xs mt-1">Completed</p>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-gray-600 text-xs mt-3 text-center leading-relaxed">
                  {status === 'done' ? 'This meeting is complete.' : status === 'in_progress' ? 'Currently in progress.' : 'Not yet started.'}
                </p>
              </Card>

              {/* Notes card */}
              <Card className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2"><StickyNote size={14} /> Notes</h3>
                  <span className="text-xs text-gray-500">{notes.length}</span>
                </div>

                {/* Add note input */}
                <div className="flex gap-2 mb-3">
                  <input
                    value={noteInput}
                    onChange={e => setNoteInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addNote()}
                    placeholder="Add a note…"
                    className="flex-1 bg-black border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-400 transition-colors"
                    data-testid="note-input"
                  />
                  <button onClick={addNote}
                    className="p-2 rounded-lg border border-gray-600 hover:border-gray-300 text-gray-300 hover:text-white transition-all duration-200"
                    data-testid="add-note-btn">
                    <Plus size={14} />
                  </button>
                </div>

                {/* Note list */}
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {notes.length > 0 ? notes.map(n => (
                    <div key={n.id} className={`flex items-start gap-2 p-2 rounded-lg border transition-colors ${n.done ? 'border-white/5 bg-white/3' : 'border-gray-800 hover:border-gray-600'}`}>
                      <button onClick={() => toggleNote(n.id)} className="mt-0.5 flex-shrink-0 text-gray-500 hover:text-white transition-colors">
                        {n.done ? <CheckCircle size={14} className="text-green-400" /> : <Circle size={14} />}
                      </button>
                      <span className={`flex-1 text-xs leading-relaxed ${n.done ? 'line-through text-gray-600' : 'text-gray-300'}`}>{n.text}</span>
                      <button onClick={() => removeNote(n.id)} className="flex-shrink-0 text-gray-700 hover:text-red-400 transition-colors">
                        <X size={11} />
                      </button>
                    </div>
                  )) : (
                    <p className="text-gray-700 text-xs text-center py-4">No notes yet</p>
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* ── DOCUMENTS TAB ── */}
        {activeTab === 'documents' && (
          <DocumentsTab
            meeting={meeting}
            uploadingDocument={uploadingDocument}
            documentUploadMeetingId={documentUploadMeetingId}
            handleDocumentUpload={handleDocumentUpload}
            handleRemoveDocument={handleRemoveDocument}
            apiUrl={apiUrl}
          />
        )}

        {/* ── TRANSCRIPTION TAB ── */}
        {activeTab === 'transcript' && (
          <TranscriptionsTab
            meeting={meeting}
            conversations={conversations}
            meetingTranscriptions={meetingTranscriptions}
            setMeetingTranscriptions={setMeetingTranscriptions}
            setViewingTranscription={setViewingTranscription}
            setShowTranscriptionPopup={setShowTranscriptionPopup}
            onTranscriptionUpdate={onTranscriptionUpdate}
            apiUrl={apiUrl}
          />
        )}

        {/* ── SETTINGS TAB ── */}
        {activeTab === 'settings' && (
          <Card className="p-6 max-w-lg">
            <h3 className="text-base font-semibold text-white mb-5">Settings</h3>
            <div className="space-y-4">
              <button onClick={onEdit}
                className="w-full flex items-center gap-3 p-4 rounded-lg border border-gray-700 hover:border-gray-300 hover:shadow-[0_0_12px_rgba(156,163,175,0.3)] text-white text-sm font-medium transition-all duration-200"
                data-testid="settings-edit-btn">
                <Edit2 size={15} className="text-gray-400" /> Edit Project / Meeting
              </button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
